import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import BackspaceIcon from '@mui/icons-material/Backspace';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useKioskContext } from '../context/KioskContext';

const slugToId = {
  test: '672312ec5b53fbbe37d4b0d5',
  khiang: '67d3a2144a266ea37ce7ac69',
  khiangmpob: '680e35b78a5a51ec452b6678',
  ebcbaguio: '689154b4dd8b04c79d293a3d',
};
const isMongoId = (s) => /^[a-f0-9]{24}$/i.test(s || '');

const TableNumber = ({ onTableSubmit, onBack }) => {
  const { posId } = useParams();
  const location = useLocation();
  const { translate, tableNumber, updateTableNumber } = useKioskContext();
  const [localTableNumber, setLocalTableNumber] = useState(tableNumber || '');

  const resolvedPosId = isMongoId(posId)
    ? posId
    : slugToId[(posId || '').toLowerCase()] || posId;

  // Default logo
  const defaultLogo =
    posId === 'ebcbaguio'
      ? '/assets/images/logo2.png'
      : '/assets/images/logo.png';

  // Logo state + loading
  const [logoSrc, setLogoSrc] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLogoLoading(true);
      try {
        if (!resolvedPosId) {
          if (!cancelled) {
            setLogoSrc(defaultLogo);
            setLogoLoading(false);
          }
          return;
        }
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/public/logo`,
          { params: { posId: resolvedPosId } }
        );
        const url = data?.logo?.url;
        if (!cancelled) {
          setLogoSrc(url || defaultLogo);
          setLogoLoading(false);
        }
      } catch {
        if (!cancelled) {
          setLogoSrc(defaultLogo);
          setLogoLoading(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [resolvedPosId, defaultLogo]);

  // Handle number pad button click
  const handleNumberClick = (num) => {
    if (localTableNumber.length < 4) {
      setLocalTableNumber((prev) => prev + num);
    }
  };

  const handleBackspace = () => {
    setLocalTableNumber((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    setLocalTableNumber('');
  };

  const handleSubmit = () => {
    if (localTableNumber) {
      updateTableNumber(localTableNumber);
      onTableSubmit(localTableNumber);
    }
  };

  const renderNumberPad = () => {
    const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
    return (
      <Grid container spacing={2}>
        {numbers.map((num) => (
          <Grid item xs={4} key={num}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => handleNumberClick(num)}
              sx={{
                py: 2,
                fontSize: '1.5rem',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              {num}
            </Button>
          </Grid>
        ))}
        <Grid item xs={4}>
          <Button
            variant="contained"
            color="error"
            fullWidth
            onClick={handleClear}
            sx={{
              py: 2,
              fontSize: '1.5rem',
            }}
          >
            C
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={handleBackspace}
            sx={{
              py: 2,
              fontSize: '1.5rem',
            }}
          >
            <BackspaceIcon />
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: 3,
        backgroundColor: '#f5f5f5',
      }}
    >
      {/* Logo (with loading) */}
      <Box sx={{ mb: 2, textAlign: 'center', minHeight: 80 }}>
        {logoLoading ? (
          <CircularProgress />
        ) : (
          <img
            src={logoSrc}
            alt="Sparkle Pay"
            style={{ maxWidth: '250px', marginBottom: '10px' }}
          />
        )}
      </Box>

      {/* Back Button */}
      <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
        <IconButton
          onClick={onBack}
          color="primary"
          sx={{
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'background.default' },
          }}
        >
          {/* back icon intentionally hidden per your previous UI */}
        </IconButton>
      </Box>

      <Typography variant="h3" component="h1" align="center" gutterBottom>
        {translate('Enter Table Number', {
          en: 'Enter Table Number',
          zh: '输入桌号',
          th: 'ป้อนหมายเลขโต๊ะ',
          tl: 'Ilagay ang Numero ng Mesa',
        })}
      </Typography>

      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 500,
          mx: 'auto',
          p: 3,
          borderRadius: 2,
        }}
      >
        {/* Display area */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            textAlign: 'center',
            mb: 3,
            height: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            backgroundColor: '#f9f9f9',
          }}
        >
          <Typography variant="h2">{localTableNumber || ' '}</Typography>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* Number pad */}
        {renderNumberPad()}

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="success"
            fullWidth
            size="large"
            onClick={handleSubmit}
            disabled={!localTableNumber}
            startIcon={<CheckCircleIcon />}
            sx={{
              py: 2,
              fontSize: '1.2rem',
            }}
          >
            {translate('Confirm', {
              en: 'Confirm',
              zh: '确认',
              th: 'ยืนยัน',
              tl: 'Kumpirmahin',
            })}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default TableNumber;
